import { useRef, useState } from "react";
import * as XLSX from "xlsx";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import {
  Upload, FileSpreadsheet, Download, CheckCircle2, AlertCircle, Loader2,
  ArrowLeft, FileDown, RotateCcw, Eye
} from "lucide-react";
import { toast } from "sonner";

const TEMPLATE_COLUMNS = [
  "admission_no", "first_name", "middle_name", "surname", "gender", "date_of_birth",
  "program", "class", "arm",
  "admission_date",
  "guardian_name", "guardian_relationship", "guardian_phone_1", "guardian_phone_2", "guardian_email",
  "address", "blood_group", "medical_notes", "status",
];

const TEMPLATE_SAMPLE = [
  {
    admission_no: "", first_name: "Khadija", middle_name: "Suleiman", surname: "Mohammed",
    gender: "female", date_of_birth: "2014-04-12",
    program: "Conventional School", class: "Basic 3", arm: "A",
    admission_date: "2025-09-05",
    guardian_name: "Suleiman Mohammed", guardian_relationship: "Father",
    guardian_phone_1: "+234 803 123 4567", guardian_phone_2: "",
    guardian_email: "suleiman.m@example.com",
    address: "Sabon Gari, Zaria", blood_group: "O+",
    medical_notes: "", status: "Active",
  },
  {
    admission_no: "", first_name: "Abdullahi", middle_name: "", surname: "Ibrahim",
    gender: "male", date_of_birth: "2012-09-22",
    program: "Islamiyyah", class: "Islamiyyah 2", arm: "A",
    admission_date: "2025-09-08",
    guardian_name: "Ibrahim Sani", guardian_relationship: "Father",
    guardian_phone_1: "+234 805 111 2222", guardian_phone_2: "+234 806 333 4444",
    guardian_email: "ibrahim.s@example.com",
    address: "Tudun Wada, Zaria", blood_group: "A+",
    medical_notes: "Mild asthma", status: "Active",
  },
  {
    admission_no: "DFA-2026-001", first_name: "Khadija", middle_name: "Yusuf", surname: "Mohammed",
    gender: "female", date_of_birth: "2014-04-12",
    program: "Tahfeez", class: "Beginner", arm: "Group A",
    admission_date: "2025-09-05",
    guardian_name: "Suleiman Mohammed", guardian_relationship: "Father",
    guardian_phone_1: "+234 803 123 4567", guardian_phone_2: "",
    guardian_email: "",
    address: "", blood_group: "",
    medical_notes: "", status: "Active",
  },
];

const HUMAN_HEADERS = {
  admission_no: "Admission Number",
  first_name: "First Name",
  middle_name: "Middle Name",
  surname: "Surname",
  gender: "Gender",
  date_of_birth: "Date of Birth",
  program: "Program",
  class: "Class",
  arm: "Arm/Section",
  admission_date: "Admission Date",
  guardian_name: "Guardian Name",
  guardian_relationship: "Guardian Relationship",
  guardian_phone_1: "Guardian Phone 1",
  guardian_phone_2: "Guardian Phone 2",
  guardian_email: "Guardian Email",
  address: "Address",
  blood_group: "Blood Group",
  medical_notes: "Medical Notes",
  status: "Student Status",
};

const triggerDownload = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

const buildWorkbook = (sample = true) => {
  const data = sample ? TEMPLATE_SAMPLE : [Object.fromEntries(TEMPLATE_COLUMNS.map(c => [c, ""]))];
  // Convert to human-readable headers for the file
  const rowsHuman = data.map(row => Object.fromEntries(TEMPLATE_COLUMNS.map(c => [HUMAN_HEADERS[c], row[c] ?? ""])));
  const ws = XLSX.utils.json_to_sheet(rowsHuman, { header: TEMPLATE_COLUMNS.map(c => HUMAN_HEADERS[c]) });
  // Column widths
  ws["!cols"] = TEMPLATE_COLUMNS.map(c => ({ wch: Math.max(14, HUMAN_HEADERS[c].length + 2) }));
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Student Template");
  // Reference sheet with valid program/class lists (informational)
  const ref = [
    ["DFA Student Import Template — Reference"],
    [],
    ["Allowed Programs", "Use any of these names or codes:"],
    ["Conventional School", "CONV"],
    ["Creche", "CRECHE"],
    ["Islamiyyah", "ISL"],
    ["Tahfeez", "TAHFEEZ"],
    [],
    ["Allowed Gender values:", "male / female (M/F also accepted)"],
    ["Allowed Status values:", "Active / Inactive"],
    [],
    ["Notes:"],
    ["• Admission Number left blank → auto-generated as DFA-YYYY-NNNN"],
    ["• Admission Date blank → today's date"],
    ["• Existing admission numbers will be UPDATED (toggle off to skip)"],
    ["• Class must match a class within the chosen Program (e.g. 'Basic 3' for Conventional)"],
    ["• Arm/Section must match an arm within the class (e.g. 'A', 'B', 'Group A')"],
  ];
  const refWs = XLSX.utils.aoa_to_sheet(ref);
  refWs["!cols"] = [{ wch: 30 }, { wch: 50 }];
  XLSX.utils.book_append_sheet(wb, refWs, "Reference");
  return wb;
};

const downloadTemplate = (format) => {
  const wb = buildWorkbook(true);
  if (format === "csv") {
    const ws = wb.Sheets["Student Template"];
    const csv = XLSX.utils.sheet_to_csv(ws);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    triggerDownload(blob, "dfa_student_template.csv");
  } else {
    const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const blob = new Blob([out], { type: "application/octet-stream" });
    triggerDownload(blob, "dfa_student_template.xlsx");
  }
};

const downloadFailedRows = (skipped, parsedRows) => {
  // Recover the original parsed row for each failed entry by row number
  const failed = skipped.map(s => ({
    ...(parsedRows[s.row - 1] || {}),
    error_reason: s.reason,
  }));
  if (failed.length === 0) return;
  // Convert keys back to human headers + add error column
  const data = failed.map(r => {
    const out = {};
    TEMPLATE_COLUMNS.forEach(c => { out[HUMAN_HEADERS[c]] = r[c] ?? ""; });
    out["Error Reason"] = r.error_reason || "";
    return out;
  });
  const ws = XLSX.utils.json_to_sheet(data);
  ws["!cols"] = [...TEMPLATE_COLUMNS.map(c => ({ wch: Math.max(14, HUMAN_HEADERS[c].length + 2) })), { wch: 60 }];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, "Failed Rows");
  const out = XLSX.write(wb, { bookType: "xlsx", type: "array" });
  triggerDownload(new Blob([out], { type: "application/octet-stream" }), "dfa_student_failed_rows.xlsx");
};

const normalizeKey = (k) => String(k).trim().toLowerCase().replace(/[\s/]+/g, "_").replace(/[^a-z0-9_]/g, "");

// Map any human header variant to a canonical key the backend understands
const HEADER_ALIASES = {
  admission_number: "admission_no", admission_no: "admission_no", admission: "admission_no",
  first_name: "first_name", firstname: "first_name", given_name: "first_name",
  middle_name: "middle_name", other_names: "middle_name", middle: "middle_name",
  surname: "surname", last_name: "surname", lastname: "surname", family_name: "surname",
  gender: "gender", sex: "gender",
  date_of_birth: "date_of_birth", dob: "date_of_birth", birth_date: "date_of_birth",
  program: "program",
  class: "class", classroom: "class", grade: "class",
  arm: "arm", section: "arm", arm_section: "arm",
  admission_date: "admission_date", date_of_admission: "admission_date",
  guardian_name: "guardian_name", parent_name: "guardian_name",
  guardian_relationship: "guardian_relationship", relationship: "guardian_relationship",
  guardian_phone_1: "guardian_phone_1", guardian_phone: "guardian_phone_1", parent_phone: "guardian_phone_1", phone: "guardian_phone_1",
  guardian_phone_2: "guardian_phone_2", alt_phone: "guardian_phone_2",
  guardian_email: "guardian_email", email: "guardian_email", parent_email: "guardian_email",
  address: "address",
  blood_group: "blood_group",
  medical_notes: "medical_notes", medical: "medical_notes", health_notes: "medical_notes",
  status: "status", student_status: "status",
};

const StudentBulkUpload = ({ open, onOpenChange, onComplete }) => {
  // 0 = upload, 1 = preview, 2 = result
  const [step, setStep] = useState(0);
  const [parsedRows, setParsedRows] = useState([]);
  const [fileName, setFileName] = useState("");
  const [uploading, setUploading] = useState(false);
  const [updateExisting, setUpdateExisting] = useState(true);
  const [drag, setDrag] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const reset = () => {
    setStep(0); setParsedRows([]); setFileName(""); setResult(null); setUploading(false);
  };
  const close = () => { reset(); onOpenChange(false); };

  const parseFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array", cellDates: true });
      const ws = wb.Sheets[wb.SheetNames[0]];
      const raw = XLSX.utils.sheet_to_json(ws, { defval: "", raw: false });
      const cleaned = raw.map((r, i) => {
        const norm = {};
        Object.keys(r).forEach(k => {
          const nk = normalizeKey(k);
          const canonical = HEADER_ALIASES[nk] || nk;
          let v = r[k];
          if (v instanceof Date) v = v.toISOString().slice(0, 10);
          if (typeof v === "string") v = v.trim();
          if (v !== "" && v != null) norm[canonical] = v;
        });
        const reasons = [];
        const first = norm.first_name;
        const last = norm.surname || norm.last_name;
        if (!first && !last && !norm.full_name) reasons.push("Missing First & Surname");
        const gender = (norm.gender || "").toString().toLowerCase();
        if (!gender) reasons.push("Missing Gender");
        else if (!"mf".includes(gender[0])) reasons.push(`Invalid Gender '${norm.gender}'`);
        if (norm.program && !norm.class) reasons.push("Program given without Class");
        return { ...norm, _row: i + 1, _valid: reasons.length === 0, _reason: reasons.join(" · ") };
      });
      setParsedRows(cleaned);
      const valid = cleaned.filter(r => r._valid).length;
      toast.success(`Parsed ${cleaned.length} rows — ${valid} valid, ${cleaned.length - valid} need fixing`);
      setStep(1);
    } catch (e) {
      console.error(e);
      toast.error("Could not parse file. Use the DFA template (xlsx or csv).");
    }
  };

  const onFilePick = (e) => {
    const f = e.target.files?.[0];
    parseFile(f);
    e.target.value = "";
  };

  const onDrop = (e) => {
    e.preventDefault(); setDrag(false);
    parseFile(e.dataTransfer.files?.[0]);
  };

  const submit = async () => {
    const validRows = parsedRows.filter(r => r._valid).map(r => {
      // Map canonical keys back to backend-friendly keys
      const m = { ...r };
      delete m._row; delete m._valid; delete m._reason;
      // backend accepts both surname/last_name → backend's normalize handles it
      return m;
    });
    if (validRows.length === 0) return toast.error("No valid rows to import");
    setUploading(true);
    try {
      const { data } = await api.post("/students/bulk", { rows: validRows, update_existing: updateExisting });
      setResult(data); setStep(2);
      toast.success(`Imported: ${data.created_count} new · ${data.updated_count} updated · ${data.skipped_count} skipped`);
      onComplete?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setUploading(false); }
  };

  const validCount = parsedRows.filter(r => r._valid).length;
  const invalidCount = parsedRows.length - validCount;

  return (
    <Dialog open={open} onOpenChange={(v) => v ? onOpenChange(true) : close()}>
      <DialogContent className="max-w-5xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-[#1B2C5A] flex items-center gap-2">
            <FileSpreadsheet className="w-5 h-5 text-[#9DCD3F]" /> Bulk Student Import
          </DialogTitle>
        </DialogHeader>

        {/* Stepper */}
        <div className="flex items-center gap-2 mb-2 text-xs">
          {["Upload", "Preview", "Result"].map((label, i) => (
            <div key={label} className="flex items-center gap-2">
              <div className={"w-6 h-6 rounded-full grid place-items-center font-semibold " + (
                step === i ? "bg-[#1B2C5A] text-white" : step > i ? "bg-[#9DCD3F] text-[#1B2C5A]" : "bg-slate-200 text-slate-600"
              )}>{i + 1}</div>
              <span className={step === i ? "text-[#1B2C5A] font-medium" : "text-[#475569]"}>{label}</span>
              {i < 2 && <div className="w-8 h-px bg-slate-300" />}
            </div>
          ))}
        </div>

        {/* STEP 0 — UPLOAD */}
        {step === 0 && (
          <div className="space-y-4">
            <div className="rounded-xl bg-[#FDFBF7] border border-[#E2E8F0] p-4">
              <h4 className="text-sm font-medium text-[#1B2C5A]">1. Download the DFA Student Import Template</h4>
              <p className="text-xs text-[#475569] mt-1">Fill it out offline, then upload. The template includes sample rows + a Reference sheet listing valid Programs and Statuses.</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                <Button size="sm" onClick={() => downloadTemplate("xlsx")} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="bulk-template-xlsx">
                  <Download className="w-4 h-4 mr-1.5" /> Download Excel (.xlsx)
                </Button>
                <Button size="sm" variant="outline" onClick={() => downloadTemplate("csv")} data-testid="bulk-template-csv">
                  <Download className="w-4 h-4 mr-1.5" /> Download CSV
                </Button>
              </div>
            </div>

            <div
              onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
              onDragLeave={() => setDrag(false)}
              onDrop={onDrop}
              className={"rounded-xl border-2 border-dashed p-8 text-center transition " + (drag ? "border-[#9DCD3F] bg-[#9DCD3F]/5" : "border-[#E2E8F0] bg-white hover:border-[#1B2C5A]/40")}
              data-testid="bulk-dropzone"
            >
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFilePick} data-testid="bulk-file-input" />
              <div className="w-12 h-12 rounded-full bg-[#9DCD3F]/15 grid place-items-center mx-auto">
                <Upload className="w-5 h-5 text-[#1B2C5A]" />
              </div>
              <h4 className="font-medium text-[#1B2C5A] mt-3">2. Drop your completed file here</h4>
              <p className="text-xs text-[#475569] mt-1">Accepts .xlsx, .xls, or .csv · Up to 2000 rows per upload.</p>
              <Button onClick={() => fileRef.current?.click()} className="mt-4 bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="bulk-pick-file-btn">
                <FileSpreadsheet className="w-4 h-4 mr-1.5" /> Choose File
              </Button>
              {fileName && <p className="text-xs text-[#1B2C5A] mt-3">Selected: <strong>{fileName}</strong></p>}
            </div>
          </div>
        )}

        {/* STEP 1 — PREVIEW */}
        {step === 1 && (
          <div className="space-y-3">
            <div className="grid grid-cols-3 gap-3">
              <Pill label="Total Rows" value={parsedRows.length} tone="navy" />
              <Pill label="Valid" value={validCount} tone="green" />
              <Pill label="Need Fixing" value={invalidCount} tone="red" />
            </div>

            <label className="flex items-center gap-2 text-sm text-[#1B2C5A]" data-testid="bulk-update-existing">
              <input type="checkbox" checked={updateExisting} onChange={(e) => setUpdateExisting(e.target.checked)} />
              Update records when Admission Number already exists (otherwise skip duplicates)
            </label>

            <div className="rounded-lg border border-[#E2E8F0] overflow-hidden max-h-[50vh] overflow-y-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#FDFBF7] sticky top-0">
                    <TableHead className="w-12">#</TableHead>
                    <TableHead className="w-20">Status</TableHead>
                    <TableHead>Admission</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Gender</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Class · Arm</TableHead>
                    <TableHead>Guardian</TableHead>
                    <TableHead>Issue</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {parsedRows.map((r) => (
                    <TableRow key={r._row} className={r._valid ? "" : "bg-red-50/40"} data-testid={`bulk-row-${r._row}`}>
                      <TableCell className="text-xs text-[#475569]">{r._row}</TableCell>
                      <TableCell>
                        {r._valid
                          ? <span className="inline-flex items-center gap-1 text-xs text-emerald-700"><CheckCircle2 className="w-3.5 h-3.5" /> OK</span>
                          : <span className="inline-flex items-center gap-1 text-xs text-red-700"><AlertCircle className="w-3.5 h-3.5" /> Error</span>}
                      </TableCell>
                      <TableCell className="font-mono text-xs">{r.admission_no || <span className="text-[#475569]">auto</span>}</TableCell>
                      <TableCell className="font-medium text-[#1B2C5A]">{[r.first_name, r.middle_name, r.surname || r.last_name].filter(Boolean).join(" ") || "—"}</TableCell>
                      <TableCell className="capitalize text-xs">{r.gender || "—"}</TableCell>
                      <TableCell className="text-xs">{r.program || "—"}</TableCell>
                      <TableCell className="text-xs">{r.class ? `${r.class}${r.arm ? ` · ${r.arm}` : ""}` : "—"}</TableCell>
                      <TableCell className="text-xs">{r.guardian_name || "—"}</TableCell>
                      <TableCell className="text-xs text-red-700">{r._reason || ""}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

        {/* STEP 2 — RESULT */}
        {step === 2 && result && (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-3">
              <Pill label="Created" value={result.created_count} tone="green" />
              <Pill label="Updated" value={result.updated_count} tone="navy" />
              <Pill label="Skipped" value={result.skipped_count} tone="red" />
              <Pill label="Total" value={result.total} tone="slate" />
            </div>
            {result.skipped?.length > 0 && (
              <div className="rounded-lg border border-red-200 bg-red-50/30 p-3">
                <div className="flex items-center justify-between mb-2">
                  <h5 className="font-medium text-red-800 inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4" /> Skipped rows</h5>
                  <Button size="sm" variant="outline" onClick={() => downloadFailedRows(result.skipped, parsedRows)} className="border-red-200 text-red-700" data-testid="bulk-download-failed">
                    <FileDown className="w-3.5 h-3.5 mr-1" /> Download for correction
                  </Button>
                </div>
                <div className="max-h-40 overflow-y-auto text-xs">
                  {result.skipped.map((s) => (
                    <div key={s.row} className="flex gap-3 py-0.5">
                      <span className="text-red-700 font-medium w-12">Row {s.row}</span>
                      <span className="text-[#475569]">{s.reason}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(result.created?.length > 0 || result.updated?.length > 0) && (
              <div className="rounded-lg border border-emerald-200 bg-emerald-50/30 p-3 max-h-48 overflow-y-auto">
                <h5 className="font-medium text-emerald-800 inline-flex items-center gap-1.5 mb-2"><CheckCircle2 className="w-4 h-4" /> Successful imports ({(result.created?.length || 0) + (result.updated?.length || 0)})</h5>
                <ul className="text-xs space-y-0.5">
                  {result.created.map((c) => (
                    <li key={"c"+c.row} className="text-[#1B2C5A]"><span className="font-mono text-emerald-700">{c.admission_no}</span> — {c.student.full_name} <em className="text-[#475569]">(new)</em></li>
                  ))}
                  {result.updated.map((u) => (
                    <li key={"u"+u.row} className="text-[#1B2C5A]"><span className="font-mono text-blue-700">{u.admission_no}</span> — {u.student.full_name} <em className="text-[#475569]">(updated)</em></li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 0 && (
            <Button variant="outline" onClick={close}>Cancel</Button>
          )}
          {step === 1 && (
            <>
              <Button variant="outline" onClick={() => { setStep(0); setParsedRows([]); }} data-testid="bulk-back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" /> Back</Button>
              <Button onClick={submit} disabled={uploading || validCount === 0} className="bg-[#9DCD3F] hover:bg-[#88BB35] text-[#1B2C5A] font-medium" data-testid="bulk-submit-btn">
                {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Importing…</> : <><Upload className="w-4 h-4 mr-1.5" /> Import {validCount} student{validCount !== 1 ? "s" : ""}</>}
              </Button>
            </>
          )}
          {step === 2 && (
            <>
              <Button variant="outline" onClick={() => { setStep(0); setParsedRows([]); setResult(null); }} data-testid="bulk-restart-btn"><RotateCcw className="w-4 h-4 mr-1.5" /> Import another file</Button>
              <Button onClick={close} className="bg-[#1B2C5A] hover:bg-[#14224A] text-white" data-testid="bulk-done-btn">Done</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const Pill = ({ label, value, tone }) => {
  const tones = {
    navy: "bg-[#1B2C5A] text-white",
    green: "bg-emerald-100 text-emerald-800 border-emerald-200",
    red: "bg-red-100 text-red-800 border-red-200",
    slate: "bg-slate-100 text-slate-800 border-slate-200",
  };
  return (
    <div className={"rounded-xl px-4 py-3 border " + (tones[tone] || tones.slate)}>
      <div className="text-[10px] uppercase tracking-wider opacity-80">{label}</div>
      <div className="text-2xl font-display mt-0.5">{value}</div>
    </div>
  );
};

export default StudentBulkUpload;
